import { fetchJSON as fetchAPI } from './client';

export type OptimizationDashboard = {
  tenant_id: string;
  open_recommendations: number;
  executed_optimizations: number;
  total_savings_usd: number;
  average_confidence: number;
  platform_health_score: number;
  self_healing_events: number;
  capacity_alerts: number;
  performance_score: number;
  autonomous_mode: string;
  kill_switch_active: boolean;
  generated_at: string;
  platform_health?: { module: string; status: string; latency_ms?: number }[];
};

export type Recommendation = {
  id: string;
  title: string;
  description?: string;
  category: string;
  module: string;
  status: string;
  confidence: number;
  impact_score: number;
  savings_usd?: number;
  reversible: boolean;
  actions?: string[];
};

export type CostAnalysis = {
  id: string;
  module: string;
  current_usd: number;
  projected_usd: number;
  savings_usd: number;
  period: string;
  breakdown?: Record<string, number>;
};

export type CapacityForecast = {
  id: string;
  resource: string;
  current_usage: number;
  forecast_30d: number;
  forecast_90d: number;
  unit: string;
  recommendation?: string;
};

export type PerformanceProfile = {
  id: string;
  module: string;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  throughput_rps: number;
  error_rate: number;
  score: number;
};

export type OptimizationJob = {
  id: string;
  name: string;
  category: string;
  status: string;
  mode: string;
  confidence: number;
  savings_usd?: number;
  started_at: string;
};

export type PlatformBenchmark = {
  id: string;
  name: string;
  module: string;
  baseline: number;
  current: number;
  improvement_pct: number;
  unit: string;
};

export type SelfHealingAction = {
  id: string;
  target: string;
  action: string;
  status: string;
  trigger: string;
  recovered: boolean;
  started_at: string;
};

const ROOT = import.meta.env.VITE_OPTIMIZATION_API ?? '/api/v1';

function fetchRoot<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(ROOT, path, init);
}

export const optimizationApi = {
  dashboard: () => fetchRoot<OptimizationDashboard>('/optimization/dashboard'),
  recommendations: () => fetchRoot<Recommendation[]>('/optimization/recommendations'),
  capacity: () => fetchRoot<CapacityForecast[]>('/optimization/capacity'),
  cost: () => fetchRoot<CostAnalysis[]>('/optimization/cost'),
  execute: (body: { recommendation_id: string; mode?: string }) =>
    fetchRoot<OptimizationJob>('/optimization/execute', { method: 'POST', body: JSON.stringify(body) }),
  history: () => fetchRoot<OptimizationJob[]>('/optimization/history'),
  benchmarks: () => fetchRoot<PlatformBenchmark[]>('/optimization/benchmarks'),
  selfHealing: () => fetchRoot<SelfHealingAction[]>('/optimization/self-healing'),
  triggerHealing: (body?: { target?: string; trigger?: string }) =>
    fetchRoot<SelfHealingAction>('/optimization/self-healing', { method: 'POST', body: JSON.stringify(body ?? {}) }),
  performance: () => fetchRoot<PerformanceProfile[]>('/optimization/performance'),
  activity: () => fetchRoot<{ id: string; action: string; message: string; created_at: string }[]>('/optimization/activity'),
};
